import { Link } from 'react-router-dom';
import { Shield, Sparkles, Heart, Eye, Mail, MapPin, Phone, ArrowRight, Target, Users, TrendingUp, Award } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';

const team = [
  { name: 'Alex Rivera', title: 'CEO & Co-founder', initials: 'AR', color: 'var(--sky)', bg: 'var(--sky-light)', bio: 'Former VP of Sales at Salesforce. 15 years of experience building sales technology that empowers teams to sell smarter, not harder.' },
  { name: 'Priya Patel', title: 'CTO & Co-founder', initials: 'PP', color: 'var(--green)', bg: 'var(--green-light)', bio: 'Ex-Google ML engineer. Built AI systems processing billions of data points. Passionate about applying machine learning to real business problems.' },
  { name: 'James Chen', title: 'VP of Product', initials: 'JC', color: 'var(--amber)', bg: 'var(--amber-light)', bio: 'Previously led product at Outreach.io. Deep expertise in sales engagement platforms and user-centric product design.' },
  { name: 'Sofia Martinez', title: 'VP of Customer Success', initials: 'SM', color: '#7C3AED', bg: '#EDE9FE', bio: '10+ years helping enterprise sales teams adopt new technology. Obsessed with customer outcomes and making sure every user gets value from day one.' }
];

const values = [
  { Icon: Shield, title: 'Compliance-first', color: 'var(--sky)', bg: 'var(--sky-light)', desc: 'We take data privacy seriously. LeadNova is SOC 2 Type II certified, GDPR compliant, and follows CAN-SPAM regulations.' },
  { Icon: Sparkles, title: 'AI-powered', color: 'var(--amber)', bg: 'var(--amber-light)', desc: 'We believe AI should augment human intelligence, not replace it. Our AI tools make sales teams more effective while keeping humans in control.' },
  { Icon: Heart, title: 'Customer-centric', color: '#E53E6B', bg: '#FDE8EE', desc: 'Every feature we build starts with a customer problem. We measure success by the outcomes our customers achieve.' },
  { Icon: Eye, title: 'Transparent', color: 'var(--green)', bg: 'var(--green-light)', desc: 'No hidden fees, no dark patterns, no bait-and-switch. We believe in honest pricing, clear communication, and building trust.' }
];

const stats = [
  { value: '2,000+', label: 'Sales teams', Icon: Users },
  { value: '210M+', label: 'Contacts in database', Icon: Target },
  { value: '3x', label: 'Average reply increase', Icon: TrendingUp },
  { value: '$2.4B', label: 'Pipeline generated', Icon: Award },
];

export default function AboutPage() {
  const { isAuthenticated } = useAuth();
  return (
    <div className="ab">
      <section className="ab-hero">
        <div className="ab-hero-bg" />
        <div className="ab-hero-dots" />
        <div className="container ab-hero-inner">
          <div className="ab-hero-pill">
            <Heart size={14} />
            About Us
          </div>
          <h1 className="ab-hero-title">We're on a mission to democratize outbound sales</h1>
          <p className="ab-hero-desc">
            LeadNova gives every sales team—from solo founders to enterprise organizations—the AI-powered tools they need to build a predictable, scalable pipeline.
          </p>
        </div>
      </section>

      <section className="ab-stats-section">
        <div className="container">
          <div className="ab-stats-grid">
            {stats.map((s) => {
              const SIcon = s.Icon;
              return (
                <div key={s.label} className="ab-stat-card">
                  <div className="ab-stat-icon-wrap"><SIcon size={20} /></div>
                  <div className="ab-stat-value">{s.value}</div>
                  <div className="ab-stat-label">{s.label}</div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      <section className="ab-section ab-story-section">
        <div className="container">
          <div className="ab-story-layout">
            <div className="ab-story-sidebar">
              <div className="ab-pill" style={{ background: 'var(--sky-light)', color: 'var(--sky)' }}>
                <Sparkles size={14} />
                Our Story
              </div>
              <h2 className="ab-story-title">Built by operators, for operators</h2>
              <p className="ab-story-highlight">
                Two founders who experienced firsthand the pain of scaling outbound sales decided to build the tool they always wished existed.
              </p>
            </div>
            <div className="ab-story-content">
              <p className="ab-story-text">
                LeadNova was founded in 2023 by Alex Rivera and Priya Patel. Alex spent 15 years building and leading sales teams at companies like Salesforce, while Priya built AI systems at Google that processed billions of data points.
              </p>
              <p className="ab-story-text">
                They saw the same problem everywhere: sales teams were drowning in manual research, sending generic outreach, and wasting hours on prospects that would never convert. The tools that existed were either too expensive for growing teams or too basic to make a real impact.
              </p>
              <p className="ab-story-text">
                So they built LeadNova—an all-in-one platform that combines AI-powered lead discovery with intelligent outreach automation. Today, over 2,000 sales teams use LeadNova to discover better leads, craft more personalized outreach, and close more deals.
              </p>
            </div>
          </div>
        </div>
      </section>

      <section className="ab-section ab-team-section">
        <div className="container">
          <div className="ab-section-header">
            <div className="ab-pill" style={{ background: 'var(--green-light)', color: 'var(--green)' }}>
              <Users size={14} />
              Our Team
            </div>
            <h2 className="ab-section-title">Meet the people behind LeadNova</h2>
            <p className="ab-section-desc">A world-class team passionate about helping sales teams succeed.</p>
          </div>
          <div className="ab-team-grid">
            {team.map((m) => (
              <div key={m.name} className="ab-team-card">
                <div className="ab-avatar" style={{ background: m.bg, color: m.color }}>
                  {m.initials}
                </div>
                <h3 className="ab-member-name">{m.name}</h3>
                <p className="ab-member-title">{m.title}</p>
                <p className="ab-member-bio">{m.bio}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="ab-section ab-values-section">
        <div className="container">
          <div className="ab-section-header">
            <div className="ab-pill" style={{ background: 'var(--amber-light)', color: 'var(--amber)' }}>
              <Shield size={14} />
              Our Values
            </div>
            <h2 className="ab-section-title">The principles that guide everything we do</h2>
          </div>
          <div className="ab-values-grid">
            {values.map((v) => {
              const VIcon = v.Icon;
              return (
                <div key={v.title} className="ab-value-card">
                  <div className="ab-value-icon" style={{ background: v.bg, color: v.color }}>
                    <VIcon size={22} />
                  </div>
                  <h3 className="ab-value-title">{v.title}</h3>
                  <p className="ab-value-desc">{v.desc}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      <section className="ab-section ab-contact-section">
        <div className="container">
          <div className="ab-section-header">
            <h2 className="ab-section-title">Get in touch</h2>
          </div>
          <div className="ab-contact-grid">
            <div className="ab-contact-card">
              <div className="ab-contact-icon"><Mail size={22} /></div>
              <h4 className="ab-contact-label">Email</h4>
              <p className="ab-contact-value">info@leadnova.co</p>
            </div>
            <div className="ab-contact-card">
              <div className="ab-contact-icon"><MapPin size={22} /></div>
              <h4 className="ab-contact-label">Office</h4>
              <p className="ab-contact-value">Suite 100, 10394-105st, Edmonton, Alberta, Canada</p>
            </div>
            <div className="ab-contact-card">
              <div className="ab-contact-icon"><Phone size={22} /></div>
              <h4 className="ab-contact-label">Phone</h4>
              <p className="ab-contact-value">(415) 555-0123</p>
            </div>
          </div>
          <div className="ab-contact-cta">
            <Link to="/contact" className="ab-btn-primary">
              Contact Us <ArrowRight size={18} />
            </Link>
          </div>
        </div>
      </section>

      <section className="ab-cta-section">
        <div className="ab-cta-glow" />
        <div className="container ab-cta-inner">
          <h2 className="ab-cta-title">Ready to transform your outbound?</h2>
          <p className="ab-cta-desc">Join 2,000+ sales teams already using LeadNova to crush their quotas.</p>
          {!isAuthenticated && (
            <Link to="/pricing" className="ab-btn-cta">
              Start Free Trial <ArrowRight size={18} />
            </Link>
          )}
        </div>
      </section>

      <style>{`
        .ab { overflow-x: hidden; }

        .ab-hero {
          background: var(--navy); position: relative;
          padding: 140px 0 80px; text-align: center; overflow: hidden;
        }
        .ab-hero-bg {
          position: absolute; inset: 0;
          background: radial-gradient(ellipse 80% 60% at 50% 0%, rgba(77,184,232,0.18), transparent 70%);
        }
        .ab-hero-dots {
          position: absolute; inset: 0; opacity: 0.03;
          background-image: radial-gradient(circle, #fff 1px, transparent 1px);
          background-size: 32px 32px;
        }
        .ab-hero-inner { position: relative; z-index: 1; }
        .ab-hero-pill {
          display: inline-flex; align-items: center; gap: 8px;
          background: rgba(77,184,232,0.12); border: 1px solid rgba(77,184,232,0.25);
          border-radius: 100px; padding: 6px 18px; margin-bottom: 24px;
          font-size: 13px; font-weight: 600; color: var(--sky);
        }
        .ab-hero-title {
          font-size: 48px; font-weight: 800; color: #fff;
          line-height: 1.12; max-width: 750px; margin: 0 auto 20px;
          letter-spacing: -0.03em;
        }
        .ab-hero-desc {
          font-size: 19px; color: rgba(255,255,255,0.6);
          max-width: 620px; margin: 0 auto; line-height: 1.7;
        }

        .ab-stats-section { padding: 0; position: relative; z-index: 2; }
        .ab-stats-grid {
          display: grid; grid-template-columns: repeat(4, 1fr); gap: 0;
          max-width: 900px; margin: -40px auto 0;
          background: #fff; border-radius: 20px; overflow: hidden;
          box-shadow: 0 12px 48px rgba(0,0,0,0.1);
          border: 1px solid var(--gray-100);
        }
        .ab-stat-card {
          text-align: center; padding: 32px 20px;
          border-right: 1px solid var(--gray-100);
        }
        .ab-stat-card:last-child { border-right: none; }
        .ab-stat-icon-wrap {
          width: 44px; height: 44px; border-radius: 12px;
          background: var(--sky-light); color: var(--sky);
          display: flex; align-items: center; justify-content: center;
          margin: 0 auto 12px;
        }
        .ab-stat-value { font-size: 28px; font-weight: 800; color: var(--navy); letter-spacing: -0.02em; }
        .ab-stat-label { font-size: 13px; color: var(--gray-500); font-weight: 500; margin-top: 4px; }

        .ab-section { padding: 80px 0; }
        .ab-story-section { background: #fff; padding-top: 100px; }
        .ab-team-section { background: var(--gray-50); }
        .ab-values-section { background: #fff; }
        .ab-contact-section { background: var(--gray-50); }

        .ab-section-header { text-align: center; margin-bottom: 48px; }
        .ab-pill {
          display: inline-flex; align-items: center; gap: 8px;
          border-radius: 100px; padding: 6px 16px; margin-bottom: 16px;
          font-size: 13px; font-weight: 600;
        }
        .ab-section-title {
          font-size: 36px; font-weight: 800; color: var(--navy);
          margin-bottom: 12px; letter-spacing: -0.02em;
        }
        .ab-section-desc {
          font-size: 17px; color: var(--gray-500);
          max-width: 500px; margin: 0 auto; line-height: 1.6;
        }

        .ab-story-layout {
          display: grid; grid-template-columns: 1fr 1.2fr; gap: 60px; align-items: start;
        }
        .ab-story-sidebar {}
        .ab-story-title {
          font-size: 32px; font-weight: 800; color: var(--navy);
          line-height: 1.2; margin-bottom: 16px; letter-spacing: -0.02em;
        }
        .ab-story-highlight {
          font-size: 17px; color: var(--sky); font-weight: 500; line-height: 1.6;
          padding-left: 16px; border-left: 3px solid var(--sky);
        }
        .ab-story-content {}
        .ab-story-text {
          font-size: 16px; color: var(--gray-500); line-height: 1.8; margin-bottom: 16px;
        }

        .ab-team-grid {
          display: grid; grid-template-columns: repeat(4, 1fr); gap: 24px;
        }
        .ab-team-card {
          background: #fff; border-radius: 16px; padding: 32px 24px;
          text-align: center; border: 1px solid var(--gray-100);
          transition: all 0.3s;
        }
        .ab-team-card:hover { box-shadow: 0 12px 32px rgba(0,0,0,0.06); transform: translateY(-4px); }
        .ab-avatar {
          width: 72px; height: 72px; border-radius: 50%;
          display: flex; align-items: center; justify-content: center;
          font-size: 22px; font-weight: 700; margin: 0 auto 16px;
        }
        .ab-member-name { font-size: 17px; font-weight: 700; color: var(--navy); margin-bottom: 4px; }
        .ab-member-title { font-size: 13px; color: var(--sky); font-weight: 600; margin-bottom: 14px; }
        .ab-member-bio { font-size: 14px; color: var(--gray-500); line-height: 1.6; margin: 0; }

        .ab-values-grid {
          display: grid; grid-template-columns: repeat(4, 1fr); gap: 24px;
        }
        .ab-value-card {
          background: var(--gray-50); border-radius: 16px; padding: 28px 24px;
          border: 1px solid var(--gray-100); transition: all 0.3s;
        }
        .ab-value-card:hover { border-color: var(--sky); box-shadow: 0 8px 24px rgba(77,184,232,0.06); }
        .ab-value-icon {
          width: 48px; height: 48px; border-radius: 12px;
          display: flex; align-items: center; justify-content: center;
          margin-bottom: 16px;
        }
        .ab-value-title { font-size: 17px; font-weight: 700; color: var(--navy); margin-bottom: 8px; }
        .ab-value-desc { font-size: 14px; color: var(--gray-500); line-height: 1.65; margin: 0; }

        .ab-contact-grid {
          display: grid; grid-template-columns: repeat(3, 1fr); gap: 20px;
          max-width: 720px; margin: 0 auto;
        }
        .ab-contact-card {
          background: #fff; border-radius: 16px; padding: 28px 20px;
          text-align: center; border: 1px solid var(--gray-100);
          transition: all 0.2s;
        }
        .ab-contact-card:hover { box-shadow: 0 8px 20px rgba(0,0,0,0.04); }
        .ab-contact-icon {
          width: 48px; height: 48px; border-radius: 12px;
          background: var(--sky-light); color: var(--sky);
          display: flex; align-items: center; justify-content: center;
          margin: 0 auto 12px;
        }
        .ab-contact-label { font-size: 15px; font-weight: 700; color: var(--navy); margin-bottom: 4px; }
        .ab-contact-value { font-size: 14px; color: var(--gray-500); margin: 0; }
        .ab-contact-cta { text-align: center; margin-top: 36px; }

        .ab-btn-primary {
          display: inline-flex; align-items: center; gap: 8px;
          background: var(--green); color: #fff;
          font-size: 16px; font-weight: 600; padding: 14px 32px;
          border-radius: 10px; text-decoration: none;
          box-shadow: 0 4px 14px rgba(58,170,53,0.4); transition: all 0.2s;
        }
        .ab-btn-primary:hover { text-decoration: none; opacity: 0.9; }

        .ab-cta-section {
          background: var(--navy); padding: 80px 0;
          text-align: center; position: relative; overflow: hidden;
        }
        .ab-cta-glow {
          position: absolute; inset: 0;
          background: radial-gradient(ellipse 60% 50% at 50% 50%, rgba(77,184,232,0.12), transparent);
        }
        .ab-cta-inner { position: relative; z-index: 1; }
        .ab-cta-title { font-size: 38px; font-weight: 800; color: #fff; margin-bottom: 16px; letter-spacing: -0.02em; }
        .ab-cta-desc { font-size: 18px; color: rgba(255,255,255,0.6); max-width: 500px; margin: 0 auto 32px; }
        .ab-btn-cta {
          display: inline-flex; align-items: center; gap: 8px;
          background: var(--green); color: #fff;
          font-size: 16px; font-weight: 700; padding: 14px 32px;
          border-radius: 10px; text-decoration: none;
          box-shadow: 0 4px 14px rgba(58,170,53,0.4); transition: all 0.2s;
        }
        .ab-btn-cta:hover { text-decoration: none; opacity: 0.9; }

        /* ========== TABLET (768px) ========== */
        @media (max-width: 768px) {
          .ab-hero { padding: 120px 0 60px; }
          .ab-hero-title { font-size: 34px; }
          .ab-hero-desc { font-size: 16px; }

          .ab-stats-grid { grid-template-columns: 1fr 1fr; margin-top: -30px; }
          .ab-stat-card { padding: 24px 16px; }
          .ab-stat-card:nth-child(2) { border-right: none; }
          .ab-stat-card:nth-child(-n+2) { border-bottom: 1px solid var(--gray-100); }
          .ab-stat-value { font-size: 24px; }

          .ab-section { padding: 56px 0; }
          .ab-story-section { padding-top: 72px; }
          .ab-section-title { font-size: 28px; }
          .ab-section-desc { font-size: 15px; }

          .ab-story-layout { grid-template-columns: 1fr; gap: 32px; }
          .ab-story-title { font-size: 26px; }

          .ab-team-grid { grid-template-columns: 1fr 1fr; gap: 16px; }
          .ab-team-card { padding: 24px 18px; }

          .ab-values-grid { grid-template-columns: 1fr 1fr; gap: 16px; }
          .ab-value-card { padding: 24px 18px; }

          .ab-contact-grid { grid-template-columns: 1fr; max-width: 320px; gap: 12px; }

          .ab-cta-title { font-size: 28px; }
          .ab-cta-desc { font-size: 16px; }
        }

        /* ========== SMALL MOBILE (480px) ========== */
        @media (max-width: 480px) {
          .ab-hero { padding: 110px 0 48px; }
          .ab-hero-title { font-size: 28px; }
          .ab-hero-desc { font-size: 15px; }

          .ab-stats-grid { grid-template-columns: 1fr 1fr; }
          .ab-stat-card { padding: 20px 12px; }
          .ab-stat-value { font-size: 22px; }
          .ab-stat-label { font-size: 12px; }

          .ab-section { padding: 40px 0; }
          .ab-story-section { padding-top: 56px; }
          .ab-section-header { margin-bottom: 32px; }
          .ab-section-title { font-size: 24px; }

          .ab-story-title { font-size: 22px; }
          .ab-story-highlight { font-size: 15px; }
          .ab-story-text { font-size: 15px; }

          .ab-team-grid { grid-template-columns: 1fr; gap: 14px; }
          .ab-team-card { padding: 24px 20px; }

          .ab-values-grid { grid-template-columns: 1fr; gap: 12px; }

          .ab-cta-title { font-size: 24px; }
          .ab-cta-desc { font-size: 15px; }
          .ab-btn-cta, .ab-btn-primary { padding: 12px 24px; font-size: 15px; }
        }
      `}</style>
    </div>
  );
}
